import { createColumnHelper } from "@tanstack/react-table";
import { Badge, Button, OverlayTrigger, Tooltip } from "react-bootstrap";

import { GoogleDriveFilePreviewPopover } from "@/components/GoogleDriveFilePreviewPopover";
import { Icon } from "@/components/Icon";
import { SosEntityLink } from "@/components/SosEntityLink";
import { CustomFieldValueMap } from "@/types/sos-types";
import { VendorAssessmentLogEntry } from "./vendor-assessment-log-query";

export type VendorAssessmentStatus =
  | Exclude<CustomFieldValueMap["assessmentStatus"]["value"], null | "">
  | "Not Started"
  | "Unknown";

export interface VendorAssessmentLogTableRow extends VendorAssessmentLogEntry {
  assessmentLink: string;
  assessmentStatus: VendorAssessmentStatus;
  currencyFlag?: string;
}

const columnHelper = createColumnHelper<VendorAssessmentLogTableRow>();

export const vendorAssessmentLogColumns = [
  columnHelper.accessor("name", {
    header: "Vendor Name",
    cell: ({ row, getValue }) => <SosEntityLink type="vendor" id={row.original.id} label={getValue()} />,
  }),
  columnHelper.accessor("currency.code", {
    header: "Currency",
    cell: ({ row, getValue }) => [getValue(), row.original.currencyFlag].filter(Boolean).join(" "),
  }),
  columnHelper.accessor("notes", { header: "Notes" }),
  columnHelper.accessor("archived", {
    header: "Archived",
    cell: ({ getValue }) => (getValue() ? <Icon icon="archive-fill" /> : null),
    meta: {
      cellProps: { className: "text-center" },
    },
  }),
  columnHelper.accessor("assessmentLink", {
    header: "Assessment",
    cell: ({ row, getValue }) => {
      const assessmentUrl = getValue();
      return (
        assessmentUrl ?
          <>
            <GoogleDriveFilePreviewPopover
              id={`vendor-assessment-${row.original.id.toFixed(0)}`}
              url={assessmentUrl}
              popoverPlacement="left"
            >
              <Button variant="link" className="p-0 ms-2">
                <Icon icon="eye" />
              </Button>
            </GoogleDriveFilePreviewPopover>{" "}
            <OverlayTrigger overlay={<Tooltip>Open In New Tab</Tooltip>}>
              <a href={assessmentUrl} target="_blank" rel="noreferrer">
                <Icon icon="box-arrow-up-right" />
              </a>
            </OverlayTrigger>
          </>
        : row.original.assessmentStatus === "Not Needed" ? "N/A"
        : <OverlayTrigger overlay={<Tooltip>Assessment Missing</Tooltip>}>
            <span>
              <Icon icon="x-circle" className="text-danger" />
            </span>
          </OverlayTrigger>
      );
    },
    meta: {
      cellProps: { className: "text-center" },
    },
  }),
  columnHelper.accessor("assessmentStatus", {
    header: "Assessment Status",
    cell: ({ getValue }) => {
      const status = getValue();
      const bg =
        status === "Complete - Approved" ? "success"
        : status === "In-Progress" ? "warning"
        : status === "Not Needed" ? "secondary"
        : "danger";
      return <Badge bg={bg}>{status}</Badge>;
    },
    meta: {
      cellProps: { className: "text-center" },
    },
  }),
];
